<script setup lang="ts">
import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useMessageCenterStore } from '@/store';
import AutoComplete from 'primevue/autocomplete';
import Initials from '@/components/Initials';
import InputMessage from '../Chat/components/InputMessage.vue';
import { NewChatSelectedPeople, RecipientData } from '@/types';
import { can } from '@/composables';
import { PERMISSIONS } from '@/enum';

const messageCenter = useMessageCenterStore();
const { recipients, error } = storeToRefs(messageCenter);

const hasError = computed(() => !!error.value);

const selectedPeople = ref([]);
const sendingMessage = ref(false);
const autocomplete = ref();

const selectedPeopleIds = computed(() => {
  const data: NewChatSelectedPeople = {
    singlePeopleIds: [],
    teamsNames: [],
    teamsParticipants: [],
    teams: [],
  };

  selectedPeople.value.map((recipient: RecipientData) => {
    if (recipient.ids) {
      data.teamsParticipants.push(...recipient.ids);
      data.teamsNames.push(recipient.name);
      const team = {
        person_id: recipient.person_id,
        participants: recipient.ids,
      };
      data.teams.push(team);
    } else {
      data.singlePeopleIds.push(recipient.id);
    }
  });

  return data;
});

const filteredPeople = ref();

const removeError = () => {
  messageCenter.error = undefined;
};

const startNewChat = async (messageData: FormData) => {
  let memberIds = selectedPeopleIds.value.singlePeopleIds;
  let teamNames = selectedPeopleIds.value.teamsNames.join(', ');
  memberIds = memberIds.concat(selectedPeopleIds.value.teamsParticipants);

  if (memberIds.length === 0) {
    messageCenter.doToast('Please select at least one person or team to start a chat.', 'Error', true);
    return;
  }

  sendingMessage.value = true;

  const newChat = new FormData();
  newChat.append('name', teamNames);
  memberIds.forEach((id) => newChat.append('members[]', id.toString()));
  newChat.append('content', messageData.get('content') || '');
  newChat.append('type', messageData.get('type') || 'text');
  if (messageData.get('attachment[]')) {
    for (let i = 0; i < messageData.getAll('attachment[]').length; i++) {
      newChat.append('attachment[]', messageData.getAll('attachment[]')[i] as Blob);
    }
  }

  messageCenter.messageText = '';

  messageCenter.createNewChat(newChat);

  if (hasError.value) {
    const message = error.value.data.errors
      ? Object.values(error.value.data.errors).flat().join(', ')
      : error.value.data.message;

    messageCenter.doToast(message, 'Error', true, 5000);
    removeError();
  }

  selectedPeople.value = [];
  messageCenter.screen = 'list-chats';
  sendingMessage.value = false;
};

const mountRecipientsTeams = (recipients: RecipientData[]) => {
  const recipientsClone = [...recipients];

  for (const recipient of recipientsClone) {
    if (recipient.associated.length > 0) {
      recipient.associated.unshift(recipient.team);
    }
  }
  return recipientsClone;
};

const filterPeople = async (event) => {
  await messageCenter.getAvailableMembers(event.query);
  filteredPeople.value = mountRecipientsTeams(recipients.value);
};

const selectPerson = (event, item) => {
  if (checkSelected(item)) return;
  autocomplete.value.onOptionSelect(event, item);
};

const checkSelected = (data) => {
  return (
    selectedPeopleIds.value.singlePeopleIds.includes(data.id) ||
    (data.ids && selectedPeopleIds.value.teamsNames.includes(data.name))
  );
};
</script>

<template>
  <div
    v-if="can([PERMISSIONS.CHAT_AVAILABLE_MEMBERS, PERMISSIONS.CHAT_ADD_MEMBER])"
    class="flex flex-column justify-content-around min-h-full fadeinright"
  >
    <AutoComplete
      ref="autocomplete"
      v-model="selectedPeople"
      :suggestions="filteredPeople"
      :multiple="true"
      :min-length="3"
      field="name"
      :delay="500"
      :scroll-height="'600px'"
      option-group-label="name"
      option-group-children="associated"
      input-class="h-full"
      panel-class="new-message-autocomplete new-chat"
      class="pb-3 mt-1 new-message-recipients"
      @complete="filterPeople($event)"
    >
      <template #chip="slotProps">
        <div class="flex align-items-center">
          <Icon v-if="slotProps.value.ids" name="group" class="icon-group" />
          <span class="text-sm">{{ slotProps.value.name }}</span>
        </div>
      </template>

      <template #optiongroup="slotProps">
        <div
          class="flex justify-content-between align-items-center cursor-pointer text-sm p-3 row-select"
          :class="{ selected: checkSelected(slotProps.option) }"
        >
          <div class="flex align-items-center justify-content-center">
            <Initials :initials="slotProps.option.initials" :size="18" />
            <span
              v-if="slotProps.option.user"
              class="px-2 font-semibold"
              @click="selectPerson($event, slotProps.option)"
            >
              {{ slotProps.option.name }}
            </span>
            <span v-else class="px-2 font-semibold">
              {{ slotProps.option.name }}
            </span>
            <span v-if="slotProps.option.selected">Selected</span>
          </div>
          <span class="font-light text-xs">{{ slotProps.option.type }}</span>
        </div>
      </template>

      <template #option="slotProps">
        <div
          class="flex justify-content-between align-items-center cursor-pointer text-sm p-3 row-select"
          :class="{ selected: checkSelected(slotProps.option) }"
        >
          <div class="flex align-items-center justify-content-center pl-4">
            <Initials v-if="slotProps.option.initials" :initials="slotProps.option.initials" :size="18" />
            <Icon v-else name="group" class="text-medium" />
            <span class="px-2">{{ slotProps.option.name }}</span>
            <span v-if="slotProps.option.selected">Selected</span>
          </div>
          <span v-if="slotProps.option.initials" class="font-light text-xs">{{ slotProps.option.type }}</span>
          <div v-if="!slotProps.option.initials" class="info-icon-container">
            <Icon
              v-tooltip.left="{
                value: 'A student\'s care team includes all service providers and guardians connected to the student',
                pt: {
                  text: {
                    class: 'custom-tooltip-text',
                  },
                  arrow: {
                    class: 'custom-tooltip-arrow',
                  },
                },
              }"
              name="info"
              class="text-gray-500 text-s"
            />
          </div>
        </div>
      </template>
    </AutoComplete>
    <div class="bg-my-gray border-round-md p-1 flex-column-reverse flex-grow-1 flex">
      <InputMessage v-if="can([PERMISSIONS.MESSAGE_STORE])" class="w-full" @message-sent="startNewChat" />
    </div>
  </div>
  <Loader
    v-if="sendingMessage"
    class="absolute left-0 top-0 flex justify-content-center align-items-center w-full h-full loader"
  />
</template>

<style lang="scss" scoped>
.info-icon-container {
  height: 2rem;
  width: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover {
    .p-tooltip {
      display: block !important;
      position: absolute;
      right: 3rem;
    }
  }
}
.p-autocomplete .p-inputtext {
  height: auto;
}
.selected {
  background: var(--primary-color-light);
}

.row-select {
  height: 32px;
}

.user-button {
  font-size: 10px;
}
.new-message-autocomplete .p-autocomplete-items .text-medium {
  color: var(--gray-700);
}
</style>

<style lang="scss">
.new-message-autocomplete.p-autocomplete-panel .p-autocomplete-items .p-autocomplete-item {
  padding: 5px 0px;
  overflow: visible;
}
.new-message-autocomplete.p-autocomplete-panel .p-autocomplete-item-group {
  padding: 5px 0px;
}

.new-message-recipients {
  width: 100%;
  ul.p-autocomplete-multiple-container {
    width: 100%;
    height: 100%;
    &::before {
      content: 'To:';
      color: var(--gray-500);
      margin-top: 1px;
    }
    li {
      input {
        font-weight: bold;
      }
    }
  }
}

.custom-tooltip-text {
  max-width: 200px !important;
  text-align: left !important;
  transform: translateY(23%) !important;
}
</style>
